import { nodeToSemantic } from "./nodeToSemantic";
import { semanticToSympyCode } from "./semanticToSympyCode";
import { SympyBuildError } from "./shared";

import type { SemanticExpr } from "../types";
import type { NodeJson } from "@corca/data";

// Open-interval brackets: stored prop is single-char `]` (left) / `[` (right),
// or round `(` / `)`. Closed intervals use `[` (left) / `]` (right).
const LEFT_OPEN_OPENING = new Set(["]", "("]);
const RIGHT_OPEN_CLOSING = new Set(["[", ")"]);

const isComma = (n: NodeJson) =>
  n.type === "operatorSymbol" &&
  n.children[0]?.type === "symbol" &&
  n.children[0].props.value === ",";

/**
 * Translate an interval group node (`[a,b]`, `]a,b[`, half-open mixes) into a
 * `sp.Interval(a, b, left_open, right_open)` string. Returns null if the group
 * is not a single inline with exactly two comma-separated endpoints.
 */
export function intervalGroupToSympy(
  node: NodeJson,
  variables: Map<string, string>,
): string | null {
  if (node.type !== "group") return null;

  // Group content is wrapped in a 1x1 matrix: group > matrix > inline > [...].
  const matrix = node.children.find((n) => n.type === "matrix");
  const inline = matrix?.children.find((n) => n.type === "inline");
  if (!inline) return null;

  const inlineChildren = inline.children;
  const commaIndex = inlineChildren.findIndex(isComma);
  if (commaIndex < 0) return null;

  const leftNodes = inlineChildren.slice(0, commaIndex);
  const rightNodes = inlineChildren.slice(commaIndex + 1);
  if (leftNodes.length === 0 || rightNodes.length === 0) return null;
  if (rightNodes.some(isComma)) return null;

  const toExpr = (children: NodeJson[]): SemanticExpr =>
    nodeToSemantic({ type: "inline", props: {}, children }, variables, false).expr;

  const leftOpen = LEFT_OPEN_OPENING.has(node.props.openingSymbol ?? "");
  const rightOpen = RIGHT_OPEN_CLOSING.has(node.props.closingSymbol ?? "");

  const boolNode = (value: boolean): SemanticExpr => ({
    type: "operator",
    name: value ? "BooleanTrue" : "BooleanFalse",
    args: [],
  });

  // An endpoint that doesn't translate is normal while typing (`x ∈ [a, b]` with a half-typed
  // `b`), and both `nodeToSemantic` and `semanticToSympyCode` report it by throwing. Callers
  // treat this group as "not an interval", so turn it back into the documented null.
  try {
    const left = toExpr(leftNodes);
    const right = toExpr(rightNodes);

    const intervalExpr: SemanticExpr = {
      type: "operator",
      name: "Interval",
      args: [left, right, boolNode(leftOpen), boolNode(rightOpen)],
    };

    // Render endpoints (and the whole expr) verbatim using the same variable allocations.
    const leftStr = semanticToSympyCode(left, variables);
    const rightStr = semanticToSympyCode(right, variables);
    if (!leftStr || !rightStr) return null;

    return semanticToSympyCode(intervalExpr, variables);
  } catch (error) {
    if (!(error instanceof SympyBuildError)) throw error;
    return null;
  }
}

// Interval content is wrapped as group > matrix > inline > [left, comma, right].
// Splits it into the two endpoint node lists, or null if the shape doesn't match
// or there is more than one comma.
function splitIntervalGroup(node: NodeJson): {
  leftNodes: NodeJson[];
  rightNodes: NodeJson[];
  openingSymbol: string;
  closingSymbol: string;
} | null {
  if (node.type !== "group") return null;

  const matrix = node.children.find((n) => n.type === "matrix");
  const inline = matrix?.children.find((n) => n.type === "inline");
  if (!inline) return null;

  const commaIndex = inline.children.findIndex(isComma);
  if (commaIndex < 0) return null;

  const leftNodes = inline.children.slice(0, commaIndex);
  const rightNodes = inline.children.slice(commaIndex + 1);
  if (leftNodes.length === 0 || rightNodes.length === 0) return null;
  if (rightNodes.some(isComma)) return null;

  return {
    leftNodes,
    rightNodes,
    openingSymbol: node.props.openingSymbol ?? "",
    closingSymbol: node.props.closingSymbol ?? "",
  };
}

const isMinus = (n: NodeJson) =>
  n.type === "operatorSymbol" &&
  n.children[0]?.type === "symbol" &&
  n.children[0].props.value === "-";

// The editor never stores a signed value on a single number node, so a negative endpoint
// (`-3`) arrives as a leading minus operatorSymbol plus a number, not a signed literal.
function integerEndpointValue(children: NodeJson[]): number | null {
  let nodes = children;
  let sign = 1;
  if (nodes.length === 2 && isMinus(nodes[0])) {
    sign = -1;
    nodes = nodes.slice(1);
  }
  if (nodes.length !== 1) return null;
  const node = nodes[0];
  if (node.type !== "number" || node.children.length > 0) return null;
  const raw = node.props.value?.toString() ?? "";
  if (!/^\d+$/.test(raw)) return null;
  const value = sign * Number(raw);
  if (!Number.isSafeInteger(value)) return null;
  return value;
}

export function tryParseClosedIntegerInterval(node: NodeJson): { lo: number; hi: number } | null {
  const split = splitIntervalGroup(node);
  if (!split) return null;
  if (LEFT_OPEN_OPENING.has(split.openingSymbol)) return null;
  if (RIGHT_OPEN_CLOSING.has(split.closingSymbol)) return null;

  const lo = integerEndpointValue(split.leftNodes);
  const hi = integerEndpointValue(split.rightNodes);
  if (lo === null || hi === null || lo > hi) return null;

  return { lo, hi };
}

const isPositiveInfinity = (children: NodeJson[]) => {
  if (children.length !== 1) return false;
  const node = children[0];
  if (node.type !== "constant") return false;
  const symbol = node.children[0];
  return symbol?.type === "symbol" && symbol.props.key === "constant-infinity";
};

// The sequence is unbounded above, so the caller enumerates only a capped window from `lo`.
export function tryParseInfiniteUpperInterval(node: NodeJson): { lo: number } | null {
  const split = splitIntervalGroup(node);
  if (!split) return null;
  if (LEFT_OPEN_OPENING.has(split.openingSymbol)) return null;
  // Infinity is never included: the bracket must be open at the upper end.
  if (!RIGHT_OPEN_CLOSING.has(split.closingSymbol)) return null;

  if (!isPositiveInfinity(split.rightNodes)) return null;

  const lo = integerEndpointValue(split.leftNodes);
  if (lo === null) return null;

  return { lo };
}

// -∞ has no dedicated constant: it is a leading minus plus the same infinity constant.
const isNegativeInfinity = (children: NodeJson[]) =>
  children.length === 2 && isMinus(children[0]) && isPositiveInfinity(children.slice(1));

// The sequence is unbounded below, so the caller enumerates a capped window ending at `hi`.
export function tryParseInfiniteLowerInterval(node: NodeJson): { hi: number } | null {
  const split = splitIntervalGroup(node);
  if (!split) return null;
  // -∞ is never included: the bracket must be open at the lower end.
  if (!LEFT_OPEN_OPENING.has(split.openingSymbol)) return null;
  if (RIGHT_OPEN_CLOSING.has(split.closingSymbol)) return null;

  if (!isNegativeInfinity(split.leftNodes)) return null;

  const hi = integerEndpointValue(split.rightNodes);
  if (hi === null) return null;

  return { hi };
}
